/**
 * CrossAppLinks — "Also in:" row on LitCurrent article cards
 *
 * Icon shortcuts to open the same article in other LitCurrent apps.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { navV2Routes, type AppKey } from '../hooks/useNavV2';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { FileSearch, BookOpen, Headphones, GraduationCap, MessageSquare, Globe } from 'lucide-react';

interface CrossAppLinksProps {
  pmid: string;
  profileId?: string | null;
  originApp: AppKey;
  /** Article title — used for LitPortal deep link */
  title?: string;
  className?: string;
}

const DESTINATIONS: Array<{ app: AppKey; label: string; icon: React.ElementType; color: string }> = [
  { app: 'screen',        label: 'LitCurrent',   icon: FileSearch,    color: 'text-sky-600 hover:bg-sky-50' },
  { app: 'learn',         label: 'LitHub',     icon: BookOpen,      color: 'text-emerald-600 hover:bg-emerald-50' },
  { app: 'audio-digests', label: 'LitStream',  icon: Headphones,    color: 'text-violet-600 hover:bg-violet-50' },
  { app: 'litscholar',    label: 'LitScholar', icon: GraduationCap, color: 'text-rose-700 hover:bg-rose-50' },
  { app: 'community',     label: 'LitClub',   icon: MessageSquare, color: 'text-amber-600 hover:bg-amber-50' },
  { app: 'litportal',     label: 'LitPortal',  icon: Globe,         color: 'text-teal-700 hover:bg-teal-50' },
];

function legacyArticleRoute(app: AppKey, pmid: string, profileId?: string | null, title?: string): string {
  const qs = new URLSearchParams();
  if (profileId) qs.set('profileId', profileId);
  switch (app) {
    case 'litportal': {
      qs.set('pmid', pmid);
      if (title?.trim()) qs.set('q', title.trim());
      return `/litportal?${qs.toString()}`;
    }
    case 'litscholar':
      return `/litscholar?pmid=${encodeURIComponent(pmid)}`;
    case 'community':
      return `/community/new?pmid=${encodeURIComponent(pmid)}`;
    case 'screen':
      qs.set('pmid', pmid);
      return `/screen?${qs.toString()}`;
    case 'learn':
      return `/library/articles/${encodeURIComponent(pmid)}${profileId ? `?profileId=${encodeURIComponent(profileId)}` : ''}`;
    case 'audio-digests':
      qs.set('pmid', pmid);
      return `/audio-digests?${qs.toString()}`;
    default:
      return '/library';
  }
}

export const CrossAppLinks: React.FC<CrossAppLinksProps> = ({
  pmid,
  profileId,
  originApp,
  title,
  className,
}) => {
  const navigate = useNavigate();
  const navV2Enabled = useFeatureFlag('enable_navigation_v2');
  const links = DESTINATIONS.filter((d) => d.app !== originApp);

  const goToApp = (app: AppKey) => {
    const path = navV2Enabled
      ? navV2Routes.articleDetail(app, pmid, profileId || undefined, originApp)
      : legacyArticleRoute(app, pmid, profileId, title);
    navigate(path);
  };

  return (
    <div
      className={`flex items-center gap-2 px-5 py-2 border-t border-slate-100 bg-slate-50/60 ${className || ''}`}
      data-testid={`cross-app-links-${pmid}`}
    >
      <span className="text-xs text-slate-500 shrink-0">Also in:</span>
      <div className="flex items-center gap-1.5 flex-wrap">
        {links.map(({ app, label, icon: Icon, color }) => (
          <button
            key={app}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goToApp(app);
            }}
            title={`Open in ${label}`}
            aria-label={`Open in ${label}`}
            className={`p-1.5 rounded-md border border-transparent hover:border-slate-200 transition-colors ${color}`}
            data-testid={`cross-app-${app}-${pmid}`}
          >
            <Icon className="w-4 h-4" strokeWidth={1.75} />
          </button>
        ))}
      </div>
    </div>
  );
};

export default CrossAppLinks;
